import React from "react";
import { Segmented, Space, Tabs } from "antd";
import { useSearchParams } from "react-router-dom";

import PageHeader from "../../../components/PageHeader";
import { GameQuizKind } from "../../../services/gameQuizAttempts/gameQuizAttempts.types";
import {
  EducationLevel,
  ResourceSessionType,
} from "../../../services/resourceSessions/resourceSessions.types";
import QuizAttemptsTab, { QUIZ_TAB_PARAMS } from "./QuizAttemptsTab";
import ResourceSessionsTab, {
  RESOURCE_TAB_PARAMS,
} from "./ResourceSessionsTab";

type SessionTab =
  | { key: string; label: string; quiz: GameQuizKind }
  | { key: string; label: string; resource: ResourceSessionType };

/**
 * Lo que cada nivel registra (SCRUM-1167). Primaria resuelve videos y lecturas
 * como quiz, así que sus videoquizzes y lecturas son intentos de quiz; Kinder
 * los mira o lee sin quiz y solo deja la primera vez que los terminó.
 */
const TABS_BY_LEVEL: Record<EducationLevel, SessionTab[]> = {
  PRIMARIA: [
    { key: "quizzes", label: "Quizzes", quiz: "QUIZ" },
    { key: "videoquizzes", label: "Videoquizzes", quiz: "VIDEOQUIZ" },
    { key: "lecturas", label: "Lecturas", quiz: "READING" },
    { key: "juegos", label: "Juegos", resource: "GAME" },
  ],
  KINDER: [
    { key: "videos", label: "Videos", resource: "VIDEO" },
    { key: "juegos", label: "Juegos", resource: "GAME" },
    { key: "lecturas", label: "Lecturas", resource: "READING" },
  ],
};

const LEVEL_OPTIONS: { value: EducationLevel; label: string }[] = [
  { value: "PRIMARIA", label: "Primaria" },
  { value: "KINDER", label: "Kinder" },
];

/** Cambiar de nivel o de pestaña limpia lo que solo valía en la anterior. */
const TAB_SCOPED_PARAMS = ["page", ...QUIZ_TAB_PARAMS, ...RESOURCE_TAB_PARAMS];

const Attempts: React.FC = () => {
  // Nivel y pestaña viven en la URL para que la vista sea deep-linkeable,
  // igual que los filtros. Sin nivel es Primaria, así los links viejos
  // (?tab=juegos) siguen abriendo lo mismo; una pestaña desconocida cae en la
  // primera del nivel.
  const [searchParams, setSearchParams] = useSearchParams();
  const level: EducationLevel =
    searchParams.get("nivel") === "kinder" ? "KINDER" : "PRIMARIA";
  const tabs = TABS_BY_LEVEL[level];
  const activeTab =
    tabs.find((tab) => tab.key === searchParams.get("tab")) ?? tabs[0];

  const navigate = (nextLevel: EducationLevel, nextTab: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        TAB_SCOPED_PARAMS.forEach((param) => next.delete(param));
        if (nextLevel === "KINDER") next.set("nivel", "kinder");
        else next.delete("nivel");
        if (nextTab === TABS_BY_LEVEL[nextLevel][0].key) next.delete("tab");
        else next.set("tab", nextTab);
        return next;
      },
      { replace: true },
    );
  };

  const handleLevelChange = (nextLevel: EducationLevel) => {
    // Juegos y Lecturas existen en los dos niveles: se queda en la misma.
    const sameTab = TABS_BY_LEVEL[nextLevel].find(
      (tab) => tab.key === activeTab.key,
    );
    navigate(nextLevel, (sameTab ?? TABS_BY_LEVEL[nextLevel][0]).key);
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <PageHeader title="Sesiones" />

      <Segmented<EducationLevel>
        options={LEVEL_OPTIONS}
        value={level}
        onChange={handleLevelChange}
      />

      <Tabs
        activeKey={activeTab.key}
        onChange={(key) => navigate(level, key)}
        // Cada pestaña lee los mismos filtros de la URL: montadas a la vez,
        // todas pedirían su listado con cada cambio de filtro.
        destroyInactiveTabPane
        items={tabs.map((tab) => ({
          key: tab.key,
          label: tab.label,
          children:
            "quiz" in tab ? (
              <QuizAttemptsTab kind={tab.quiz} />
            ) : (
              <ResourceSessionsTab type={tab.resource} level={level} />
            ),
        }))}
      />
    </Space>
  );
};

export default Attempts;
